import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaBug, FaSearch, FaLeaf, FaFlask, FaTools, FaShoppingCart } from 'react-icons/fa';
import SummaryApi from '../common';
import service from '../common/HttpService';

const CropDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [crop, setCrop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('pests');
    const [selectedIssue, setSelectedIssue] = useState(null);

    useEffect(() => {
        const loadCrop = async () => {
            try {
                setLoading(true);
                const response = await service.get(SummaryApi.getCrop.url);
                const cropsData = response.extraData?.crop || response.data?.crop || response.crop || [];
                const apiCrop = cropsData.find(c => String(c.id || c._id) === String(id));

                if (apiCrop) {
                    const baseCrop = {
                        id: apiCrop.id || apiCrop._id,
                        name: apiCrop.name || apiCrop.crop_name,
                        image: apiCrop.image || apiCrop.crop_image
                    };

                    const types = [1, 2, 3];
                    const promises = types.map(type => {
                        const formData = new FormData();
                        formData.append('crop_id', id);
                        formData.append('type', type);
                        return service.post(SummaryApi.getPestDiseaseDeficiency.url, formData).catch(() => ({}));
                    });

                    const results = await Promise.all(promises);

                    const mapIssues = (apiArray) => {
                        return (apiArray || []).map((item, idx) => ({
                            id: `api_${idx}_${Date.now()}`,
                            name: item.name,
                            image: item.image,
                            symptoms: item.symptom || [],
                            identification: item.identification || [],
                            management: item.management || [],
                            products: item.item || [],
                            scientific: item.scientific_name || ''
                        }));
                    };

                    const isSuccess = (res) => res?.success === "true" || res?.success === true;

                    baseCrop.pests = isSuccess(results[0]) ? mapIssues(results[0].extraData) : [];
                    baseCrop.diseases = isSuccess(results[1]) ? mapIssues(results[1].extraData) : [];
                    baseCrop.deficiencies = isSuccess(results[2]) ? mapIssues(results[2].extraData) : [];

                    setCrop(baseCrop);
                }
            } catch (error) {
                console.error("Error loading crop:", error);
            } finally {
                setLoading(false);
            }
        };

        loadCrop();
        window.scrollTo(0, 0);
    }, [id]);

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen">
                {/* Breadcrumb Skeleton */}
                <div className="bg-white border-b border-gray-100">
                    <div className="container mx-auto px-4 py-3">
                        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                </div>

                <div className="container mx-auto px-4 py-8">
                    {/* Crop Header Skeleton */}
                    <div className="flex flex-col md:flex-row items-center gap-6 mb-10 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 animate-pulse">
                        <div className="w-28 h-28 rounded-full bg-gray-200 shrink-0"></div>
                        <div className="flex-1 w-full flex flex-col items-center md:items-start gap-3">
                            <div className="h-8 w-64 bg-gray-200 rounded"></div>
                            <div className="h-4 w-40 bg-gray-200 rounded"></div>
                        </div>
                    </div>

                    {/* Category Tabs Skeleton */}
                    <div className="flex bg-white rounded-2xl border border-gray-100 shadow-sm mb-8 overflow-hidden animate-pulse">
                        <div className="flex-1 h-12 md:h-14 bg-gray-200 border-r border-gray-50"></div>
                        <div className="flex-1 h-12 md:h-14 bg-gray-200 border-r border-gray-50"></div>
                        <div className="flex-1 h-12 md:h-14 bg-gray-200"></div>
                    </div>

                    {/* Issues Grid Skeleton */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {Array(8).fill(null).map((_, idx) => (
                            <div key={idx} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
                                <div className="aspect-video bg-gray-200"></div>
                                <div className="p-4 flex flex-col gap-3">
                                    <div className="h-5 w-3/4 bg-gray-200 rounded"></div>
                                    <div className="h-3.5 w-full bg-gray-200 rounded"></div>
                                    <div className="h-3.5 w-5/6 bg-gray-200 rounded"></div>
                                    <div className="h-4 w-1/3 bg-gray-200 rounded mt-2"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!crop) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <h2 className="text-2xl font-bold text-gray-800">Crop Not Found</h2>
                <p className="text-gray-500 mt-2">The crop you are looking for is not in our advisory database.</p>
                <button onClick={() => navigate('/')} className="mt-6 bg-green-600 text-white px-6 py-2 rounded-full font-bold">
                    Back to Home
                </button>
            </div>
        );
    }

    const categories = [
        { id: 'pests', name: 'Pests', icon: <FaBug /> },
        { id: 'diseases', name: 'Diseases', icon: <FaSearch /> },
        { id: 'deficiencies', name: 'Deficiencies', icon: <FaLeaf /> }
    ];

    const currentIssues = crop[selectedCategory] || [];

    return (
        <div className="bg-gray-50 min-h-screen">
            {/* Breadcrumb */}
            <div className="bg-white border-b border-gray-100">
                <div className="container mx-auto px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/" className="hover:text-green-600">Home</Link>
                        <span>/</span>
                        <span className="text-gray-900 font-medium">{crop.name}</span>
                        {selectedIssue && <>
                            <span>/</span>
                            <span className="text-gray-900 font-medium">{selectedIssue.name}</span>
                        </>}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                {!selectedIssue ? (
                    <div>
                        {/* Crop Header */}
                        <div className="flex flex-col md:flex-row items-center gap-6 mb-10 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <img
                                src={crop.image}
                                alt={crop.name}
                                className="w-28 h-28 rounded-full object-cover border-4 border-green-100 shadow"
                                onError={(e) => { e.target.src = 'https://placehold.co/200x200/e8f5e9/2e7d32?text=Crop'; }}
                            />
                            <div className="text-center md:text-left">
                                <h1 className="text-4xl font-black text-gray-900 uppercase tracking-tight">
                                    {crop.name}
                                </h1>
                                <p className="text-gray-400 mt-1 text-sm font-bold uppercase tracking-widest">Expert Care & Identification</p>
                            </div>
                        </div>

                        {/* Category Tabs */}
                        <div className="flex bg-white rounded-2xl border border-gray-100 shadow-sm mb-8">
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-6 py-3 md:py-4 text-xs md:text-base font-bold uppercase tracking-wider transition-all rounded-2xl ${selectedCategory === cat.id
                                        ? 'bg-green-600 text-white shadow-md'
                                        : 'text-gray-400 hover:text-gray-700'
                                        }`}
                                >
                                    {cat.icon}
                                    {cat.name} ({crop[cat.id]?.length || 0})
                                </button>
                            ))}
                        </div>

                        {/* Issues Grid */}
                        {currentIssues.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                                {currentIssues.map((issue) => (
                                    <div
                                        key={issue.id}
                                        onClick={() => { setSelectedIssue(issue); window.scrollTo(0, 0); }}
                                        className="group bg-white cursor-pointer rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg hover:border-green-200 transition-all"
                                    >
                                        <div className="aspect-video relative overflow-hidden bg-gray-100">
                                            <img
                                                src={issue.image}
                                                alt={issue.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                onError={(e) => { e.target.src = 'https://placehold.co/400x250/e8f5e9/2e7d32?text=Issue'; }}
                                            />
                                            {issue.products?.length > 0 && (
                                                <span className="absolute top-3 right-3 bg-green-600 text-white text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">
                                                    {issue.products.length} solutions
                                                </span>
                                            )}
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-black text-gray-900 group-hover:text-green-600 transition-colors text-base uppercase tracking-tight">{issue.name}</h3>
                                            {issue.symptoms?.[0] && (
                                                <p className="text-sm text-gray-400 mt-1.5 line-clamp-2 leading-relaxed">{issue.symptoms[0]}</p>
                                            )}
                                            <div className="mt-3 flex items-center gap-1 text-green-600 text-sm font-bold">
                                                View Details <FaArrowLeft size={12} className="rotate-180" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
                                <p className="text-gray-400 font-medium italic">No information available for this category yet.</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        {/* Back Button */}
                        <button
                            onClick={() => setSelectedIssue(null)}
                            className="flex items-center gap-2 text-green-600 font-bold text-sm mb-6 hover:underline"
                        >
                            <FaArrowLeft size={12} /> Back to {crop.name} Advisory
                        </button>

                        {/* Issue Detail Header */}
                        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 mb-6">
                            <div className="flex flex-col md:flex-row gap-6">
                                <div className="md:w-64 shrink-0">
                                    <div className="rounded-2xl overflow-hidden shadow-md aspect-square">
                                        <img
                                            src={selectedIssue.image}
                                            alt={selectedIssue.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.src = 'https://placehold.co/400x400/e8f5e9/2e7d32?text=Issue'; }}
                                        />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <span className="bg-green-100 text-green-700 text-xs font-black px-3 py-1 rounded-full uppercase tracking-widest">
                                        {selectedCategory === 'pests' ? 'Pest' : selectedCategory === 'diseases' ? 'Disease' : 'Deficiency'}
                                    </span>
                                    <h2 className="text-4xl font-black text-gray-900 mt-2 uppercase tracking-tight">{selectedIssue.name}</h2>
                                    {selectedIssue.scientific && (
                                        <p className="text-green-600 italic font-medium mt-1 text-base">{selectedIssue.scientific}</p>
                                    )}

                                    {/* Symptoms */}
                                    {selectedIssue.symptoms?.length > 0 && (
                                        <div className="mt-5">
                                            <h4 className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-gray-400 mb-3">
                                                <FaSearch className="text-blue-500" /> Symptoms
                                            </h4>
                                            <ul className="space-y-2">
                                                {selectedIssue.symptoms.map((s, i) => (
                                                    <li key={i} className="flex gap-3 text-gray-700 text-base">
                                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 shrink-0" />
                                                        <span>{s}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Identification & Management Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            {/* Identification */}
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                                <h4 className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-gray-400 mb-4">
                                    <FaTools className="text-orange-500" /> Identification
                                </h4>
                                {selectedIssue.identification?.length > 0 ? (
                                    <ul className="space-y-2">
                                        {selectedIssue.identification.map((s, i) => (
                                            <li key={i} className="flex gap-3 text-gray-700 text-base">
                                                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 shrink-0" />
                                                <span>{s}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-gray-400 italic text-base">No identification details available.</p>
                                )}
                            </div>

                            {/* Management */}
                            <div className="bg-green-50 rounded-2xl p-6 shadow-sm border border-green-100">
                                <h4 className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-green-700 mb-4">
                                    <FaFlask className="text-green-600" /> Management &amp; Control
                                </h4>
                                {selectedIssue.management?.length > 0 ? (
                                    <ul className="space-y-2">
                                        {selectedIssue.management.map((s, i) => (
                                            <li key={i} className="flex gap-3 text-gray-800 text-base font-medium">
                                                <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 shrink-0" />
                                                <span>{s}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-gray-400 italic text-sm">No management guidelines provided.</p>
                                )}
                            </div>
                        </div>

                        {/* Recommended Products */}
                        {selectedIssue.products?.length > 0 && (
                            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                                <h4 className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-400 mb-6">
                                    <FaShoppingCart className="text-green-600" /> Recommended Products ({selectedIssue.products.length})
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {selectedIssue.products.map((product) => (
                                        <div
                                            key={product.id}
                                            onClick={() => navigate(`/product/${product.id}`)}
                                            className="group cursor-pointer flex gap-4 p-4 rounded-xl border border-gray-100 hover:border-green-200 hover:bg-green-50 transition-all"
                                        >
                                            <div className="w-20 h-20 bg-white border border-gray-100 rounded-xl overflow-hidden flex items-center justify-center shrink-0 shadow-sm">
                                                <img
                                                    src={product.image}
                                                    alt={product.itemname}
                                                    className="w-full h-full object-contain p-1"
                                                    onError={(e) => { e.target.src = 'https://placehold.co/100x100/f9fafb/9ca3af?text=Product'; }}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className="text-[9px] font-black text-green-700 uppercase tracking-widest bg-green-100 px-2 py-0.5 rounded-full">
                                                    {product.category_name || 'Product'}
                                                </span>
                                                <h5 className="text-sm font-bold text-gray-800 group-hover:text-green-700 transition-colors mt-1 line-clamp-2 leading-snug">
                                                    {product.itemname}
                                                </h5>
                                                {product.chemical_composition && (
                                                    <p className="text-[10px] text-gray-400 mt-1 line-clamp-1">{product.chemical_composition}</p>
                                                )}
                                                <span className="flex items-center gap-1 text-[10px] text-green-600 font-bold mt-1">View Product <FaArrowLeft size={9} className="rotate-180" /></span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CropDetails;
